import { map, switchMap, take } from 'rxjs/operators';
import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { UserInfoService } from '../services/user-info.service';
import { selectUserRegionCode } from '../store/selectors/user-profile.selectors';
import { MFA_AVAILABILITY } from '../store/reducers/session-info.reducer';

/**
 * Checks the user's Multi-Factor Authentication (MFA) availability status.
 *
 * This guard implements the following redirection logic based on the user's MFA status:
 * - `NOT_AVAILABLE`: The user has not set up MFA. Redirects to the MFA setup page or partner selection page.
 * - `IN_RECOVERY`: The user is in the MFA recovery process. Redirects to the recovery page.
 * - `UNKNOWN`: The user is likely a social login user. Allows access.
 * - `AVAILABLE`: The user has MFA configured. Allows access.
 *
 * This guard should be used after a primary authentication check has passed.
 */
export const mfaAvailabilityGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean | UrlTree> => {
  const router = inject(Router);
  const store = inject(Store);
  const userInfoService = inject(UserInfoService);

  return userInfoService
    .getUserInfo$('initiated from mfaAvailabilityGuard')
    .pipe(
      switchMap((userInfo) => {
        switch (userInfo.mfa_availability) {
          case MFA_AVAILABILITY.NOT_AVAILABLE: //not fully onboarded
            return store.pipe(
              selectUserRegionCode,
              take(1),
              map((region) =>
                region
                  ? router.parseUrl(`/two-factor/setup?callback=${route.url}`)
                  : router.parseUrl(`/interests/partner`),
              ),
            );

          case MFA_AVAILABILITY.IN_RECOVERY: //user is in recovery
            return of(router.parseUrl(`/two-factor/recovery`));

          case MFA_AVAILABILITY.UNKNOWN: //user is social login user
          case MFA_AVAILABILITY.AVAILABLE: //has 2fa, is third level and can continue
            return of(true);

          default:
            return of(true);
        }
      }),
    );
};
